import { CartLinesUpdateEvent } from '@shopify/events';

const MAX_RECOMMENDATION_SOURCES = 3;
const MIN_VISIBLE_RECOMMENDATIONS = 4;
const MAX_VISIBLE_RECOMMENDATIONS = 8;
let fallbackProductsRequest;

class FurseraCartPerfectMatch extends HTMLElement {
  #products = [];
  #loadedProductKey = '';
  #loadToken = 0;
  #recommendationRequests = new Map();
  #trackObserver;
  #refreshTimer;
  #prefetchIdleCallback;
  #drawer;
  #needsRefresh = false;

  #handlePrevious = () => this.#scroll(-1);
  #handleNext = () => this.#scroll(1);
  #handleDrawerToggle = () => {
    if (!this.#drawer?.open) return;
    const force = this.#needsRefresh;
    this.#needsRefresh = false;
    this.#load(force);
  };

  static get observedAttributes() {
    return ['data-product-id', 'data-product-ids'];
  }

  connectedCallback() {
    this.querySelector('[data-previous]')?.addEventListener('click', this.#handlePrevious);
    this.querySelector('[data-next]')?.addEventListener('click', this.#handleNext);
    this.#drawer = this.closest('dialog');
    this.#drawer?.addEventListener('toggle', this.#handleDrawerToggle);
    this.#observeTrack();
    if (this.#drawer?.open) this.#load();
  }

  disconnectedCallback() {
    this.querySelector('[data-previous]')?.removeEventListener('click', this.#handlePrevious);
    this.querySelector('[data-next]')?.removeEventListener('click', this.#handleNext);
    this.#drawer?.removeEventListener('toggle', this.#handleDrawerToggle);
    this.#drawer = undefined;
    this.#trackObserver?.disconnect();
    this.#trackObserver = undefined;
    window.clearTimeout(this.#refreshTimer);
    this.#refreshTimer = undefined;
    if (this.#prefetchIdleCallback) {
      window.cancelIdleCallback?.(this.#prefetchIdleCallback);
      window.clearTimeout(this.#prefetchIdleCallback);
      this.#prefetchIdleCallback = undefined;
    }
    this.#loadToken += 1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected || oldValue === newValue) return;
    if (name === 'data-product-id' || name === 'data-product-ids') this.#scheduleRefresh();
  }

  #observeTrack() {
    if (this.#trackObserver) return;
    const track = this.querySelector('[data-track]');
    if (!track) return;

    this.#trackObserver = new MutationObserver(() => {
      // Section morphing restores the server-rendered empty track. Our own render
      // leaves cards in place, so it does not schedule another request.
      if (track.childElementCount === 0) this.#scheduleRefresh();
    });
    this.#trackObserver.observe(track, { childList: true });
  }

  #scheduleRefresh() {
    if (!this.#drawer?.open) {
      this.#needsRefresh = true;
      return;
    }

    window.clearTimeout(this.#refreshTimer);
    this.#refreshTimer = window.setTimeout(() => {
      this.#refreshTimer = undefined;
      if (this.isConnected) this.#load(true);
    }, 0);
  }

  async #load(force = false) {
    const productIds = [...new Set((this.dataset.productIds || this.dataset.productId || '').split(',').filter(Boolean))];
    if (!productIds.length) return;
    const productKey = productIds.join(',');
    const sourceProductIds = productIds.slice(0, MAX_RECOMMENDATION_SOURCES);

    if (!force && this.#loadedProductKey === productKey && this.#products.length) {
      this.hidden = false;
      return;
    }

    const loadToken = ++this.#loadToken;

    try {
      const complementary = (
        await Promise.all(sourceProductIds.map((productId) => this.#fetch(productId, 'complementary')))
      ).flat();
      const cartCategories = new Set(
        (this.dataset.productTitles || '')
          .split('||')
          .map((title) => this.#category({ title }))
          .filter(Boolean)
      );
      const related = (
        await Promise.all(sourceProductIds.map((productId) => this.#fetch(productId, 'related')))
      ).flat();
      const complementaryRelated = related.filter((product) => {
        const category = this.#category(product);
        return !category || !cartCategories.has(category);
      });
      const relevantComplementary = complementary.filter((product) => {
        const category = this.#category(product);
        return !category || !cartCategories.has(category);
      });

      if (loadToken !== this.#loadToken) return;

      // Only surface complementary or cross-category products. Do not relax this
      // rule to fill the shelf: a smaller relevant set is better than bed-to-bed
      // or toy-to-toy recommendations.
      let candidates = [...relevantComplementary, ...complementaryRelated];
      this.#products = this.#selectProducts(candidates, productIds);

      // Large carts often contain products returned by the first recommendation
      // sources. Query a second small source group only when the visible result
      // still cannot fill the four-card shelf.
      if (this.#products.length < MIN_VISIBLE_RECOMMENDATIONS && productIds.length > MAX_RECOMMENDATION_SOURCES) {
        const extraSourceIds = productIds.slice(
          MAX_RECOMMENDATION_SOURCES,
          MAX_RECOMMENDATION_SOURCES * 2
        );
        const extraRecommendations = (
          await Promise.all(
            extraSourceIds.flatMap((productId) => [
              this.#fetch(productId, 'complementary'),
              this.#fetch(productId, 'related'),
            ])
          )
        ).flat();
        if (loadToken !== this.#loadToken) return;
        const relevantExtraRecommendations = extraRecommendations.filter((product) => {
          const category = this.#category(product);
          return !category || !cartCategories.has(category);
        });
        candidates = [...candidates, ...relevantExtraRecommendations];
        this.#products = this.#selectProducts(candidates, productIds);
      }

      // Shopify can return only same-category recommendations for a single-item
      // cart. Fill the remaining slots from the store catalog, while preserving
      // the same cross-category rule instead of leaving a large empty drawer.
      if (this.#products.length < MIN_VISIBLE_RECOMMENDATIONS) {
        const fallbackProducts = await this.#fetchFallbackProducts();
        if (loadToken !== this.#loadToken) return;
        const relevantFallbackProducts = fallbackProducts.filter((product) => {
          const category = this.#category(product);
          return !category || !cartCategories.has(category);
        });
        candidates = [...candidates, ...relevantFallbackProducts];
        this.#products = this.#selectProducts(candidates, productIds);
      }

      this.#loadedProductKey = productKey;
      if (!this.#products.length) {
        this.hidden = true;
        return;
      }
      this.#render();
      this.hidden = false;
    } catch (error) {
      console.warn('[cart-perfect-match] Recommendations unavailable.', error);
    }
  }

  async #fetch(productId, intent) {
    const requestKey = `${productId}:${intent}`;
    const cachedRequest = this.#recommendationRequests.get(requestKey);
    if (cachedRequest) return cachedRequest;

    const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '8');
    url.searchParams.set('intent', intent);
    const request = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : { products: [] }))
      .then((result) => result.products || [])
      .catch((error) => {
        this.#recommendationRequests.delete(requestKey);
        throw error;
      });
    this.#recommendationRequests.set(requestKey, request);
    return request;
  }

  async #fetchFallbackProducts() {
    if (!this.dataset.fallbackProductsUrl) return [];
    if (!fallbackProductsRequest) {
      fallbackProductsRequest = fetch(this.dataset.fallbackProductsUrl, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : { products: [] }))
        .then((result) =>
          (result.products || []).map((product) => ({
            ...product,
            variants: (product.variants || []).map((variant) => ({
              ...variant,
              price: Math.round(Number.parseFloat(variant.price) * 100),
              compare_at_price: variant.compare_at_price
                ? Math.round(Number.parseFloat(variant.compare_at_price) * 100)
                : null,
            })),
          }))
        )
        .catch((error) => {
          fallbackProductsRequest = undefined;
          throw error;
        });
    }
    return fallbackProductsRequest;
  }

  #selectProducts(candidates, cartIds) {
    const cartProductIds = new Set(cartIds);
    const seen = new Set();
    return candidates
      .filter((product) => {
        const id = String(product?.id || '');
        if (product?.available === false || !id || cartProductIds.has(id) || seen.has(id)) return false;
        if (!product.variants?.some((variant) => variant.available)) return false;
        seen.add(id);
        return true;
      })
      .slice(0, MAX_VISIBLE_RECOMMENDATIONS);
  }

  #category(product) {
    const text = [product?.title, product?.type, product?.product_type, ...(product?.tags || [])]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const groups = [
      ['comfort', /\b(bed|bedding|blanket|mat|couch|pillow|donut)\b/],
      ['toy', /\b(toy|ball|chew|frisbee|teaser|squeak|play)\b/],
      ['feeding', /\b(bowl|feeder|feeding|treat|lick|slow feeder|spoon)\b/],
      ['hydration', /\b(water|bottle|hydration|drinking)\b/],
      ['grooming', /\b(groom|brush|comb|shampoo|deshedding)\b/],
      ['travel', /\b(carrier|travel|leash|harness|stroller|seat cover)\b/],
      ['apparel', /\b(hoodie|coat|shirt|clothing|apparel|sweater)\b/],
      ['mobility', /\b(stair|stairs|ramp|mobility)\b/],
    ];
    return groups.find(([, pattern]) => pattern.test(text))?.[0] || '';
  }

  #render() {
    const track = this.querySelector('[data-track]');
    if (!track) return;
    track.replaceChildren(...this.#products.map((product) => this.#card(product)));
    this.#prefetchVisibleQuickAdds(track);
  }

  #prefetchVisibleQuickAdds(track) {
    const warm = () => {
      this.#prefetchIdleCallback = undefined;
      const quickAdds = [...track.querySelectorAll('quick-add-component')].slice(0, MIN_VISIBLE_RECOMMENDATIONS);
      for (const quickAdd of quickAdds) quickAdd.prefetch?.().catch(() => {});
    };

    if ('requestIdleCallback' in window) {
      this.#prefetchIdleCallback = window.requestIdleCallback(warm, { timeout: 1200 });
    } else {
      this.#prefetchIdleCallback = window.setTimeout(warm, 250);
    }
  }

  #card(product) {
    const variant = product.variants.find((item) => item.available);
    const hasOptions = product.variants.length > 1;
    const productUrl = product.url || `/products/${product.handle}`;
    const card = document.createElement('product-card');
    card.className = 'cart-perfect-match__card';
    card.dataset.productId = String(product.id);

    const link = document.createElement('a');
    link.className = 'cart-perfect-match__image-link';
    link.href = productUrl;
    link.setAttribute('ref', 'productCardLink');
    const image = document.createElement('img');
    image.className = 'cart-perfect-match__image';
    image.loading = 'lazy';
    image.width = 240;
    image.height = 240;
    image.alt = product.title;
    image.src = this.#imageUrl(variant.featured_image || product.featured_image || product.images?.[0]);
    link.append(image);

    const title = document.createElement('a');
    title.className = 'cart-perfect-match__name';
    title.href = productUrl;
    title.textContent = product.title;

    const price = document.createElement('div');
    price.className = 'cart-perfect-match__price';
    const availablePrices = product.variants.filter((item) => item.available).map((item) => Number(item.price));
    const lowestPrice = Math.min(...availablePrices);
    const hasVariablePrices = availablePrices.some((itemPrice) => itemPrice !== lowestPrice);
    price.textContent = `${hasVariablePrices ? 'From ' : ''}${this.#money(lowestPrice)}`;

    const add = document.createElement('button');
    add.className = 'cart-perfect-match__add';
    add.type = 'button';
    add.setAttribute('aria-label', hasOptions ? `Choose options for ${product.title}` : `Add ${product.title} to cart`);
    add.innerHTML = '<span aria-hidden="true">+</span>';
    if (hasOptions) {
      const quickAdd = document.createElement('quick-add-component');
      quickAdd.dataset.productTitle = product.title;
      quickAdd.dataset.productId = String(product.id);
      quickAdd.dataset.productOptionsCount = String(product.options?.length || 1);
      quickAdd.dataset.usesSellingPlans = 'false';
      quickAdd.append(add);
      add.addEventListener('click', (event) => quickAdd.handleClick(event));
      quickAdd.addEventListener('pointerenter', () => quickAdd.prefetch().catch(() => {}), { once: true });
      quickAdd.addEventListener('focusin', () => quickAdd.prefetch().catch(() => {}), { once: true });
      card.append(link, title, price, quickAdd);
    } else {
      add.addEventListener('click', () => this.#add(variant.id, add));
      card.append(link, title, price, add);
    }
    return card;
  }

  #imageUrl(image) {
    const source = typeof image === 'string' ? image : image?.src || image?.url || '';
    if (!source) return '';
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', '320');
    return url.toString();
  }

  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    }).format(cents / 100);
  }

  #scroll(direction) {
    const track = this.querySelector('[data-track]');
    const card = track?.querySelector('.cart-perfect-match__card');
    if (!track || !card) return;
    track.scrollBy({ left: direction * (card.getBoundingClientRect().width + 14), behavior: 'smooth' });
  }

  async #add(variantId, button) {
    if (!variantId || button.disabled) return;
    const status = this.querySelector('[data-status]');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add product.');
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((cartResponse) => cartResponse.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-perfect-match',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
      if (status) {
        status.textContent = 'Added to cart.';
        status.hidden = false;
      }
    } catch (error) {
      deferred.reject(error);
      if (status) {
        status.textContent = error.message || 'Unable to add product.';
        status.hidden = false;
      }
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('fursera-cart-perfect-match')) {
  customElements.define('fursera-cart-perfect-match', FurseraCartPerfectMatch);
}
